/**
 * src/services/spatial/spatialPresenceClient.ts
 *
 * The browser's client for GeoTrust's own spatial presence surface —
 * `/api/v2/spatial-objects/…` and `/api/v2/spatial-broadcasts/nearby`.
 *
 * Deliberately separate from `geoLinkSpatialObjectsAdminClient`: that one
 * talks to GeoLink with a wallet-provider key and edits configuration,
 * whereas everything here is GeoTrust runtime state — who is live, what
 * they just said, what can be heard from where. Mixing them would put a
 * GeoLink credential in front of calls that do not need one.
 *
 * Refusals are returned rather than thrown. A spatial broadcast has a
 * long list of legitimate reasons not to start — a country policy, a
 * capability switch, a rate limit — and each of them is information the
 * UI should show, not an exception to swallow.
 */

import { V2_API } from '../../v2/config';

// ── Types ────────────────────────────────────────────────────────────────────

export type SpatialBroadcastStatus =
  | 'IDLE' | 'REQUESTED' | 'STARTING' | 'LIVE' | 'ENDING' | 'ENDED' | 'FAILED';

export type SpatialBroadcastTrigger =
  | 'manual' | 'proximity' | 'event' | 'execution_rule' | 'ai_action';

/** One synthesised utterance, ready to play in the tab that asked for it. */
export interface SpatialUtteranceSpeech {
  mime: string;
  voice: string;
  durationMs: number;
  audioBase64: string;
  cached: boolean;
}

export interface SpatialSpeechQueueSnapshot {
  objectId: string;
  sessionId: string;
  speaking: { utteranceId: string; seq: number; state: string } | null;
  pending: { utteranceId: string; seq: number; characters: number }[];
  spokenCount: number;
  droppedCount: number;
  characterCount: number;
}

export interface SpatialBroadcastSession {
  id: string;
  spatialObjectId: string;
  objectName: string | null;
  broadcasterId: string;
  underlyingLiveSessionId: string;
  status: SpatialBroadcastStatus;
  triggerType: SpatialBroadcastTrigger;
  executionRuleId: number | null;
  transport: 'signaling_only' | 'sfu_ingest' | 'mock';
  visibility: string;
  title: string | null;
  description: string | null;
  anchor: { lat: number; lng: number; source: string } | null;
  countryCode: string | null;
  startedAt: string;
  lastActivityAt: string;
  endedAt: string | null;
  endedReason: string | null;
  scheduledEndAt: string | null;
  currentFloorId: string | null;
  currentZoneId: string | null;
  viewerCount: number;
  hasBroadcaster: boolean;
  utteranceCount: number;
  speech: SpatialSpeechQueueSnapshot | null;
}

export interface NearbySpatialBroadcast {
  spatialObjectId: string;
  name: string | null;
  objectType: string | null;
  countryCode: string | null;
  anchor: { type: string; coordinates: [number, number] } | null;
  distanceMeters: number;
  withinEnterRadius: boolean;
  playbackRadiusMeters: number | null;
  exitRadiusMeters: number | null;
  /** Distance-derived gain in 0..1, already shaped by the configured curve. */
  volume: number;
  volumeCurve: string;
  autoSurfacePlayer: boolean;
  /** Advisory. The browser's autoplay policy still decides. */
  autoPlayAudio: boolean;
  stopOnExit: boolean;
  fadeOnExit: boolean;
  floorAware: boolean;
  zoneAware: boolean;
  altitude: { eligible: boolean; confidence: string; band?: { low: number; high: number } };
  /**
   * Where the listener is inside the object. `floorConfidence` is the
   * honest part: `high` when an altitude band matched, `low` when the
   * nearest floor is a guess, `unknown` when nothing could be resolved.
   */
  placement: {
    floor: { id: string; name: string | null; floorNumber: number | null; offsetMeters?: number } | null;
    zone: { id: string; name: string | null } | null;
    floorConfidence: 'not_required' | 'high' | 'low' | 'unknown';
    zoneMatched: boolean | null;
  };
  agentEnabled: boolean;
  live: {
    broadcasterId: string;
    sessionId: string;
    title: string | null;
    startedAt: string;
    viewerCount: number;
    transport: string;
    utteranceCount: number;
  } | null;
  canBroadcast: boolean;
  /** Populated only for AI-agent callers, who read text instead of audio. */
  transcript: { available: boolean; latestSeq: number | null } | null;
}

/**
 * Anyone an object can encounter.
 *
 * People and roaming agents share one shape because from the object's
 * side they are the same thing — something nearby that may listen and
 * may talk. What differs is the check applied: an agent's right to
 * interact comes from its on-chain capabilities and a signed envelope,
 * a person's from the object's audience settings.
 */
export interface SpatialInteractionParticipant {
  type: 'human' | 'ai_agent';
  id: string;
  wallet?: string | null;
  sessionId?: string | null;
  location?: {
    lat: number;
    lng: number;
    altitudeMeters?: number | null;
    accuracyMeters?: number | null;
  } | null;
  capabilities?: string[];
}

/**
 * The object's own media settings, as far as a visitor needs them.
 *
 * Read from GeoLink's presence config when the session opened, so this is
 * what the operator configured rather than anything the client decided. It
 * exists so voice behaviour has one source: without it a client has to infer
 * whether an object speaks from whether audio happened to arrive, and has to
 * invent its own interruption policy.
 */
export interface SpatialSessionMedia {
  /** Whether this object speaks at all. */
  ttsEnabled: boolean;
  /**
   * What happens when something new arrives while it is still speaking.
   *
   * `queue` means let it finish. The other two mean the newer thing wins,
   * which is what makes talking over the object work.
   */
  interruptMode: 'queue' | 'interrupt' | 'replace';
}

export interface SpatialInteractionSession {
  sessionId: string;
  spatialObjectId: string;
  objectName: string | null;
  participant: { type: 'human' | 'ai_agent'; id: string };
  /** Optional so an older server, or a stored session, still parses. */
  media?: SpatialSessionMedia;
  status: 'open' | 'ended';
  startedAt: string;
  lastActivityAt: string;
  endedAt: string | null;
  endedReason: string | null;
  messageCount: number;
  remainingMessages: number;
  greeting: string | null;
  /**
   * Audio for the greeting, present only when the object has TTS enabled
   * and the caller is a human who asked to be spoken to.
   *
   * Sent once, with the session that produced it. Later reads of the
   * same session do not repeat it.
   */
  greetingSpeech?: SpatialUtteranceSpeech | null;
}

export interface SpatialTranscriptEntry {
  utteranceId: string;
  sessionId: string | null;
  seq: number;
  text: string;
  speaker: string | null;
  source: string | null;
  durationMs: number | null;
  at: string;
}

// ── Nearby actions ───────────────────────────────────────────────────────────

/**
 * One thing that could be done near a point, as the server describes
 * it.
 *
 * Mirrors `nearbyActionsService`'s projection field for field, and no
 * wider: that projection is an allowlist built to keep wallet
 * identities and execution internals out of a read surface, and a type
 * here inviting more would be an invitation to publish more.
 *
 * `cost` and `contract.parameters` are honest unknowns rather than
 * absent. The fee and the arguments are the wallet's to show before
 * anybody signs, and a missing field reads as "free" to anything
 * summarising this.
 */
export interface NearbyActionUnknown {
  known: false;
  detail?: string;
}

export interface NearbyActionEligibility {
  passing: boolean;
  reason?: string | null;
  temporal?: { state?: string | null } | null;
}

export interface NearbyActionConditions {
  spatial: { radiusMeters: number | null; minDwellSeconds: number | null } | null;
  temporal: {
    startsAt?: string | null;
    endsAt?: string | null;
    status?: string | null;
    state?: string | null;
  } | null;
}

export interface NearbyAction {
  id: string;
  kind: 'interaction' | 'contract';
  /** Present on interaction actions only. */
  verb?: string;
  label: string;
  readOnly: boolean;
  requiresSigning: boolean;
  requiresTier: string;
  cost: NearbyActionUnknown | null;
  object?: {
    id: string | null;
    name: string | null;
    type: string | null;
    /** The object's own description, clamped. Null when it has written none. */
    summary?: string | null;
  };
  contract?: {
    name: string | null;
    address: string | null;
    network: string | null;
    functionName: string | null;
    parameters: NearbyActionUnknown;
  };
  rule?: {
    id: string | number | null;
    type: string | null;
    triggerOn: string | null;
    radiusMeters: number | null;
    minDwellSeconds: number | null;
    requiresConfirmation: boolean;
    maxExecutionsPerKey: number | null;
  };
  distanceMeters: number | null;
  tier: string;
  eligibility: NearbyActionEligibility;
  conditions?: NearbyActionConditions;
  executionRuleId?: string | number | null;
  broadcast?: {
    available: boolean;
    broadcasterId: string | null;
    sessionId: string | null;
    transport: string | null;
    startedAt: string | null;
  } | null;
}

export interface NearbyActionsAnswer {
  /** What was actually searched, so an absence can be stated honestly. */
  searchedRadiusMeters: number;
  /** The tier the server resolved, which is not the one a caller asked for. */
  viewerTier: string;
  actions: NearbyAction[];
  /** Named so a partial answer cannot be read as a complete one. */
  sources: { objects: string; contractRules: string };
}

/** One AI agent on the position feed, as much of it as a sentence needs. */
export interface NearbyAgent {
  agentPk: string;
  displayName: string | null;
  distanceMeters: number | null;
  tier: string | null;
  /** `smart_account` for a passkey account, `classic` for a keypair one. */
  addressKind: 'smart_account' | 'classic' | null;
  lastSeenAt: string | null;
}

export interface NearbyAgentsAnswer {
  agents: NearbyAgent[];
  searchedRadiusMeters: number;
  /** `country` for a positional search, `agent` when asked on an agent's behalf. */
  source: 'country' | 'agent';
  /** The true count, which can exceed the listed agents. */
  totalWithinRadius: number;
  country?: string;
}

/**
 * What the deployed vault contract guarantees about one destination.
 *
 * Flags rather than prose so the wording lives with the reader. The pair
 * that matters is `depositorCanWithdraw`: it is false for a spatial object
 * and true for a country vault, and that difference is the whole reason
 * this answer exists.
 */
export interface ValueFlowGuarantee {
  contractFunction: string;
  creditedTo: string;
  depositorCanWithdraw: boolean;
  refundable: boolean;
  withdrawalFunction: string;
  withdrawableBy: string;
  expires: boolean;
  /** False for object treasuries: the chain records no depositor's share. */
  perDepositorRecord: boolean;
  feeOnDeposit?: boolean;
  withdrawalGates?: string[];
}

export interface ValueFlowAnswer {
  object: {
    id: string;
    balance: { amount: string; stroops: string; asset: { code: string | null } | null };
    canReceive: boolean;
    blocker: string | null;
    hasBeneficiary: boolean;
    country: string | null;
    /** Accepting deposits with nobody named to collect them. */
    unclaimable: boolean;
  };
  countryVault: {
    country: string | null;
    walletConnected: boolean;
    balance:
      | { ok: true; amount: string; stroops: string; asset: { code: string | null } | null }
      | { ok: false; reason: string }
      | null;
  };
  guarantees: { object: ValueFlowGuarantee; countryVault: ValueFlowGuarantee };
}

export type PresenceResult<T> =
  | ({ ok: true } & T)
  | { ok: false; reason: string; [key: string]: unknown };

// ── Plumbing ─────────────────────────────────────────────────────────────────

async function call<T>(path: string, init?: RequestInit): Promise<PresenceResult<T>> {
  let resp: Response;
  try {
    resp = await fetch(`${V2_API}${path}`, init);
  } catch (err) {
    return { ok: false, reason: 'network_error', message: (err as Error).message };
  }

  let body: any = null;
  try { body = await resp.json(); } catch { /* empty body */ }

  if (!resp.ok) {
    return {
      ...(body || {}),
      ok: false,
      reason: body?.reason || `http_${resp.status}`,
    } as PresenceResult<T>;
  }
  return (body || { ok: true }) as PresenceResult<T>;
}

function postJson<T>(path: string, body: unknown): Promise<PresenceResult<T>> {
  return call<T>(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
}

// ── Discovery ────────────────────────────────────────────────────────────────

/**
 * What can be heard from a position.
 *
 * `altitudeMeters` and `accuracyMeters` are optional and honest: when the
 * fix is poor the server widens the floor band and marks the answer
 * low-confidence rather than claiming a floor it cannot know.
 */
export async function findNearbyBroadcasts(input: {
  lat: number;
  lng: number;
  altitudeMeters?: number | null;
  accuracyMeters?: number | null;
  radiusMeters?: number;
  limit?: number;
  liveOnly?: boolean;
  participantType?: 'human' | 'ai_agent';
  wallet?: string | null;
}): Promise<PresenceResult<{ count: number; broadcasts: NearbySpatialBroadcast[] }>> {
  const query = new URLSearchParams({
    lat: String(input.lat),
    lng: String(input.lng),
  });
  if (input.altitudeMeters != null) query.set('alt', String(input.altitudeMeters));
  if (input.accuracyMeters != null) query.set('accuracy', String(input.accuracyMeters));
  if (input.radiusMeters) query.set('radius', String(input.radiusMeters));
  if (input.limit) query.set('limit', String(input.limit));
  if (input.liveOnly) query.set('liveOnly', 'true');
  if (input.participantType) query.set('participantType', input.participantType);
  if (input.wallet) query.set('wallet', input.wallet);

  return call(`/spatial-broadcasts/nearby?${query.toString()}`);
}

/**
 * What can be done near a position.
 *
 * The browser's half of the answer the AI's `get_nearby_actions` tool
 * gets, from the same service, so the chat card's shortcuts and the
 * object's own replies cannot disagree about what is available.
 *
 * Read-only. Nothing in the response can be executed: costs and
 * arguments arrive as declared unknowns, and taking an action still
 * goes through the interaction panel, its authorization and a wallet.
 *
 * The radius is the server's to choose when omitted — it snaps the
 * value to a proximity tier so that "nothing nearby" is a claim about
 * a stated distance rather than an unbounded one.
 */
export function findNearbyActions(input: {
  lat: number;
  lng: number;
  radiusMeters?: number;
}): Promise<PresenceResult<NearbyActionsAnswer>> {
  const query = new URLSearchParams({
    lat: String(input.lat),
    lng: String(input.lng),
  });
  if (input.radiusMeters) query.set('radius', String(input.radiusMeters));

  return call(`/spatial-objects/nearby-actions?${query.toString()}`);
}

/**
 * Which AI agents are moving near a position.
 *
 * Separate from `findNearbyActions` because it reads a different source —
 * GeoLink's agent feed rather than objects and contract rules — and the two
 * fail independently. An object asked whether agents were around used to
 * consult the place list, which cannot contain one, and answer that there
 * were none.
 *
 * The agent feed is indexed by country, which the server resolves from the
 * position — the browser has no boundary table and should not be sent one
 * so that a chat card can name its own country.
 *
 * A failure resolves to `ok: false`. It must not be rendered as an empty
 * list: nobody there and nobody looked are different answers.
 */
export function findNearbyAgents(input: {
  lat: number;
  lng: number;
  country?: string;
  radiusMeters?: number;
}): Promise<PresenceResult<NearbyAgentsAnswer>> {
  const query = new URLSearchParams({
    lat: String(input.lat),
    lng: String(input.lng),
  });
  if (input.country) query.set('country', input.country);
  if (input.radiusMeters) query.set('radius', String(input.radiusMeters));

  return call(`/spatial-objects/nearby-agents?${query.toString()}`);
}

/**
 * Where a deposit at this object goes, and the balances that apply.
 *
 * `wallet` reports that wallet's own country vault balance and nothing
 * else. There is deliberately no per-depositor figure for the object
 * treasury, because the chain holds none: `object_balance` is an aggregate,
 * so "you have 6 XLM in this building" is not a question anything can
 * answer, however much it sounds like one.
 */
export function getValueFlow(
  objectId: string,
  input: { wallet?: string | null; country?: string | null } = {},
): Promise<PresenceResult<ValueFlowAnswer>> {
  const query = new URLSearchParams();
  if (input.wallet) query.set('wallet', input.wallet);
  if (input.country) query.set('country', input.country);
  const qs = query.toString();

  return call(
    `/spatial-objects/${encodeURIComponent(objectId)}/value-flow${qs ? `?${qs}` : ''}`,
  );
}

// ── Lifecycle ────────────────────────────────────────────────────────────────

export function getSpatialBroadcast(
  objectId: string,
): Promise<PresenceResult<{ session: SpatialBroadcastSession | null }>> {
  return call(`/spatial-objects/${encodeURIComponent(objectId)}/broadcast`);
}

export function listActiveSpatialBroadcasts(): Promise<
  PresenceResult<{ broadcasts: SpatialBroadcastSession[] }>
> {
  return call('/spatial-objects/broadcasts/active');
}

export function startSpatialBroadcast(
  objectId: string,
  body: {
    wallet?: string | null;
    trigger?: SpatialBroadcastTrigger;
    title?: string;
    description?: string;
    transport?: string;
    floorId?: string | null;
    zoneId?: string | null;
  } = {},
): Promise<PresenceResult<{ session: SpatialBroadcastSession; alreadyLive?: boolean }>> {
  return postJson(`/spatial-objects/${encodeURIComponent(objectId)}/broadcast/start`, body);
}

export function stopSpatialBroadcast(
  objectId: string,
  reason = 'ended_user',
): Promise<PresenceResult<{ session?: SpatialBroadcastSession; alreadyStopped?: boolean }>> {
  return postJson(`/spatial-objects/${encodeURIComponent(objectId)}/broadcast/stop`, { reason });
}

// ── Speech ───────────────────────────────────────────────────────────────────

/**
 * Say something on an object's broadcast.
 *
 * Omit `text` to have the object's AI write the line; supply it to speak
 * exactly those words.
 */
export function speakOnSpatialBroadcast(
  objectId: string,
  body: {
    text?: string;
    intent?: string;
    wallet?: string | null;
    trigger?: SpatialBroadcastTrigger;
    interruptMode?: 'queue' | 'interrupt' | 'replace';
    queueMode?: 'queue' | 'drop_oldest' | 'drop_newest';
  } = {},
): Promise<PresenceResult<{
  utteranceId: string;
  seq: number;
  text: string;
  source: string;
  queue: SpatialSpeechQueueSnapshot | null;
}>> {
  return postJson(`/spatial-objects/${encodeURIComponent(objectId)}/speak`, body);
}

export function getSpeechQueue(
  objectId: string,
): Promise<PresenceResult<{ queue: SpatialSpeechQueueSnapshot | null }>> {
  return call(`/spatial-objects/${encodeURIComponent(objectId)}/speech-queue`);
}

// ── Private interaction ──────────────────────────────────────────────────────

/**
 * Open a private conversation with an object.
 *
 * Private throughout: this session has its own history and prompt mode,
 * and nothing said in it is ever published to the object's broadcast.
 */
export function startSpatialInteraction(
  objectId: string,
  participant: SpatialInteractionParticipant,
  opts: { distanceMeters?: number | null; greet?: boolean } = {},
): Promise<PresenceResult<{ session: SpatialInteractionSession }>> {
  return postJson(`/spatial-objects/${encodeURIComponent(objectId)}/interactions`, {
    participant,
    distanceMeters: opts.distanceMeters ?? null,
    greet: opts.greet,
  });
}

export function sendSpatialMessage(
  sessionId: string,
  text: string,
  opts: { speak?: boolean } = {},
): Promise<PresenceResult<{
  sessionId: string;
  reply: string;
  source: string;
  /** Delegated tool rounds the turn ran. 0 means the model answered unaided. */
  toolRounds?: number;
  /** Present only when the object has TTS enabled and the caller is human. */
  speech: SpatialUtteranceSpeech | null;
  messageCount: number;
  remainingMessages: number;
}>> {
  return postJson(`/spatial-interactions/${encodeURIComponent(sessionId)}/messages`, {
    text,
    speak: opts.speak,
  });
}

/**
 * Ask the object to speak a line the client already composed.
 *
 * For shortcut answers, which are built here from data already
 * fetched and so never pass through the model. Only the voice is
 * wanted, and the text stands on its own if it does not arrive.
 */
export function speakSpatialAside(
  sessionId: string,
  text: string,
): Promise<PresenceResult<{
  sessionId: string;
  speech: SpatialUtteranceSpeech | null;
}>> {
  return postJson(`/spatial-interactions/${encodeURIComponent(sessionId)}/speech`, {
    text,
    speak: true,
  });
}

export function endSpatialInteraction(
  sessionId: string,
  reason = 'ended_user',
): Promise<PresenceResult<{ session?: SpatialInteractionSession; alreadyEnded?: boolean }>> {
  return postJson(`/spatial-interactions/${encodeURIComponent(sessionId)}/end`, { reason });
}

/**
 * What an object has said aloud recently.
 *
 * Also the caption backfill for a listener who joined mid-broadcast:
 * the caption fan-out only reaches whoever was already in the room.
 */
export function readSpatialTranscript(
  objectId: string,
  opts: { sinceSeq?: number | null; limit?: number } = {},
): Promise<PresenceResult<{
  live: { sessionId: string; title: string | null; startedAt: string } | null;
  entries: SpatialTranscriptEntry[];
  latestSeq: number | null;
}>> {
  const query = new URLSearchParams();
  if (opts.sinceSeq != null) query.set('sinceSeq', String(opts.sinceSeq));
  if (opts.limit) query.set('limit', String(opts.limit));
  const qs = query.toString();
  return call(
    `/spatial-objects/${encodeURIComponent(objectId)}/transcript${qs ? `?${qs}` : ''}`,
  );
}

// ── Organization media ───────────────────────────────────────────────────────

/**
 * Play a recording the operator attached to this object.
 *
 * Refused unless the broadcast is running on the SFU transport: a
 * caption event can carry an audio clip but not a video track, and
 * playing only the soundtrack would look like success.
 */
export function playSpatialAsset(
  objectId: string,
  body: { recordingId: string; wallet?: string | null; loop?: boolean },
): Promise<PresenceResult<{ recordingId: string; delivery: string }>> {
  return postJson(`/spatial-objects/${encodeURIComponent(objectId)}/assets/play`, body);
}

export function stopSpatialAssets(
  objectId: string,
  reason = 'stopped',
): Promise<PresenceResult<{ stopped: number }>> {
  return postJson(`/spatial-objects/${encodeURIComponent(objectId)}/assets/stop`, { reason });
}

// ── Surface ──────────────────────────────────────────────────────────────────

export interface SpatialSurfaceState {
  config: {
    enabled: boolean;
    surfaceKind: string;
    activationMode: string;
    defaultContentRef: string | null;
    allowedContentRefs: Array<string | { ref: string; label?: string }>;
    maxActivationSeconds: number;
    cooldownSeconds: number;
  } | null;
  activation: {
    id: string;
    contentRef: string;
    surfaceKind: string;
    activatedAt: string;
    expiresAt: string;
  } | null;
}

export function getSpatialSurface(
  objectId: string,
): Promise<PresenceResult<SpatialSurfaceState>> {
  return call(`/spatial-objects/${encodeURIComponent(objectId)}/surface`);
}

/**
 * Show approved content on the object's surface.
 *
 * `contentRef` must be on the operator's allow-list. That check is
 * server-side; sending anything else comes back as `content_not_allowed`
 * rather than being silently ignored.
 */
export function activateSpatialSurface(
  objectId: string,
  body: {
    contentRef: string;
    wallet?: string | null;
    durationSeconds?: number | null;
    reason?: string | null;
  },
): Promise<PresenceResult<{ activation: SpatialSurfaceState['activation'] }>> {
  return postJson(`/spatial-objects/${encodeURIComponent(objectId)}/surface/activate`, body);
}

export function deactivateSpatialSurface(
  objectId: string,
  reason = 'ended_user',
): Promise<PresenceResult<{ alreadyInactive?: boolean }>> {
  return postJson(`/spatial-objects/${encodeURIComponent(objectId)}/surface/deactivate`, { reason });
}

// ── Orchestration ────────────────────────────────────────────────────────────

/**
 * Run surface, broadcast and speech as one occasion.
 *
 * Partial success is a real outcome and is reported as one: `succeeded`
 * and `failed` name the steps, so a caller can retry only what did not
 * land rather than re-running the whole thing.
 */
export function runSpatialOccasion(
  objectId: string,
  body: {
    wallet?: string | null;
    trigger?: SpatialBroadcastTrigger;
    reason?: string | null;
    surface?: { contentRef: string; durationSeconds?: number | null } | null;
    broadcast?: { title?: string | null; description?: string | null } | null;
    speech?: { text?: string | null; intent?: string | null } | null;
  },
): Promise<PresenceResult<{
  partial: boolean;
  succeeded: string[];
  failed: string[];
  steps: Record<string, { ok: boolean; reason?: string }>;
}>> {
  return postJson(`/spatial-objects/${encodeURIComponent(objectId)}/occasion`, body);
}

export function endSpatialOccasion(
  objectId: string,
  reason = 'ended_user',
): Promise<PresenceResult<{ steps: Record<string, unknown> }>> {
  return postJson(`/spatial-objects/${encodeURIComponent(objectId)}/occasion/end`, { reason });
}

export interface ScheduledSpatialOccasion {
  objectId: string;
  title: string | null;
  startsAt: string;
  endsAt: string | null;
  contentRef: string | null;
  wallet: string | null;
  fired?: boolean;
  ended?: boolean;
}

/**
 * Register an object's saved event with the server-side scheduler.
 *
 * Sends no times: the server re-reads `metadata.event` from GeoLink, so a
 * caller cannot schedule something nobody stored. Called after saving an
 * object so the schedule takes effect at once instead of at the next
 * reconcile.
 */
export function scheduleSpatialOccasion(
  objectId: string,
  wallet?: string | null,
): Promise<PresenceResult<{
  scheduled: boolean;
  occasion?: ScheduledSpatialOccasion;
  reason?: string;
}>> {
  return postJson(`/spatial-objects/${encodeURIComponent(objectId)}/occasion/schedule`, { wallet });
}

export function listScheduledSpatialOccasions(): Promise<
  PresenceResult<{ occasions: ScheduledSpatialOccasion[] }>
> {
  return call('/spatial-objects/occasions/scheduled');
}

// ── Test / preview ───────────────────────────────────────────────────────────

/** One verdict in the simulator panel: what it decided and why. */
export interface SpatialSimulationVerdict {
  ok?: boolean;
  passing?: boolean;
  reason?: string | null;
  forced?: boolean;
  [k: string]: unknown;
}

export interface SpatialSimulationReport {
  object: { id: string; name: string | null; objectType: string | null; countryCode: string | null };
  config: {
    agentEnabled: boolean;
    mediaEnabled: boolean;
    ttsEnabled: boolean;
    broadcastTriggerMode: string | null;
    transport: string;
  };
  proximity: {
    anchor: { lat: number; lng: number; source: string } | null;
    distanceMeters: number | null;
    playbackRadiusMeters: number | null;
    exitRadiusMeters: number | null;
    insideEnterRadius: boolean | null;
    beyondExitRadius: boolean | null;
    volume: number | null;
  };
  placement: {
    floors?: Array<{ id: string; name: string | null; floorNumber: number | null }>;
    zones?: Array<{ id: string; name: string | null }>;
    altitude?: { eligible: boolean; confidence: string };
    error?: string;
  } | null;
  jurisdiction: SpatialSimulationVerdict;
  compliance: SpatialSimulationVerdict;
  authority: SpatialSimulationVerdict & { wallet: string | null };
  rule: SpatialSimulationVerdict | null;
  audience: { participantType: string; allowed: boolean; requiresWallet: boolean; walletPresent: boolean };
  session: SpatialBroadcastSession | null;
  speech: SpatialSpeechQueueSnapshot | null;
  surface: { config: Record<string, unknown> | null; activation: Record<string, unknown> | null };
  transportState: { mode: string; audioIngestId: string | null; activeProcesses: number } | null;
  aiContext: Record<string, unknown> | null;
  /** The headline: would a listener standing here be offered a player? */
  eligible: boolean;
  blockers: string[];
  events: Array<{ at: string; type: string; payload: Record<string, unknown> }>;
}

/**
 * What would happen if a participant stood here.
 *
 * Read-only. Every verdict is produced by the same code the live path
 * runs, so this panel and reality cannot drift apart.
 */
export function inspectSpatialObject(
  objectId: string,
  opts: {
    participantType?: 'human' | 'ai_agent';
    wallet?: string | null;
    lat?: number | null;
    lng?: number | null;
    altitudeMeters?: number | null;
    accuracyMeters?: number | null;
    dwellMs?: number | null;
    forceRule?: 'pass' | 'fail' | null;
  } = {},
): Promise<PresenceResult<SpatialSimulationReport>> {
  const query = new URLSearchParams();
  if (opts.participantType) query.set('participantType', opts.participantType);
  if (opts.wallet) query.set('wallet', opts.wallet);
  if (opts.lat != null) query.set('lat', String(opts.lat));
  if (opts.lng != null) query.set('lng', String(opts.lng));
  if (opts.altitudeMeters != null) query.set('alt', String(opts.altitudeMeters));
  if (opts.accuracyMeters != null) query.set('accuracy', String(opts.accuracyMeters));
  if (opts.dwellMs != null) query.set('dwellMs', String(opts.dwellMs));
  if (opts.forceRule) query.set('forceRule', opts.forceRule);
  const qs = query.toString();
  return call(`/spatial-objects/${encodeURIComponent(objectId)}/test${qs ? `?${qs}` : ''}`);
}

export type SpatialSimulationAction =
  | 'start_broadcast'
  | 'stop_broadcast'
  | 'speak'
  | 'interact'
  | 'activate_surface'
  | 'deactivate_surface'
  | 'occasion';

/**
 * Rehearse one action.
 *
 * Mock by default: the authorization, rule and queue paths run for real
 * but nothing is published and no audio is synthesised. Pass `live` only
 * when the point is to actually be heard.
 */
export function runSpatialSimulation(
  objectId: string,
  body: {
    action: SpatialSimulationAction;
    wallet?: string | null;
    trigger?: SpatialBroadcastTrigger;
    text?: string | null;
    intent?: string | null;
    contentRef?: string | null;
    participantType?: 'human' | 'ai_agent';
    lat?: number | null;
    lng?: number | null;
    live?: boolean;
  },
): Promise<PresenceResult<{
  simulated: boolean;
  text?: string;
  source?: string;
  tts?: { mock: boolean; durationMs: number | null };
  session?: SpatialBroadcastSession;
}>> {
  return postJson(`/spatial-objects/${encodeURIComponent(objectId)}/test/action`, body);
}

/**
 * Synthesise a line and hand back the audio for local playback.
 *
 * Published nowhere — checking a voice must not be audible to whoever
 * is standing outside the building.
 */
export function previewSpatialTts(
  objectId: string,
  text: string,
  opts: { mock?: boolean } = {},
): Promise<PresenceResult<{
  text: string;
  voice: string | null;
  mime: string | null;
  durationMs: number | null;
  cached: boolean;
  playbackRate: number;
  audioBase64: string | null;
}>> {
  return postJson(`/spatial-objects/${encodeURIComponent(objectId)}/test/tts`, {
    text,
    mock: opts.mock === true,
  });
}
